/**
 * Detection des requetes qui trainent, pour avertir l'utilisateur que la
 * connexion est lente.
 *
 * Le principe : chaque requete en vol arme un minuteur au seuil. S'il se
 * declenche, la requete est comptee comme lente. Le compteur passant de 0 a 1
 * allume l'etat ; en retombant a 0, il l'eteint. Pas de scrutation periodique,
 * et le minuteur est desarme des que la requete aboutit - succes comme echec,
 * sinon une erreur reseau laisserait le bandeau colle a l'ecran.
 */

export const SEUIL_LENT_MS = 4000;

/**
 * Duree minimale d'affichage. Sans elle, une requete qui repond a 4,1 s ferait
 * apparaitre puis disparaitre le bandeau dans le meme souffle - plus
 * deroutant qu'utile.
 */
export const DUREE_MIN_AFFICHAGE_MS = 1500;

let requetes = new Map();
let nbLentes = 0;
let lent = false;
let graceEnCours = null;
const subscribers = new Set();

/** Marque, dans la table, une requete dont le minuteur a deja rendu son verdict. */
const LENTE = Symbol('lente');

const notify = () => {
  subscribers.forEach((cb) => {
    try { cb(lent); } catch (_) { /* un abonne qui jette ne doit pas casser les autres */ }
  });
};

const allumer = () => {
  lent = true;
  // Tant que la grace court, l'extinction lui est deleguee : c'est elle qui
  // garantit la duree minimale d'affichage.
  graceEnCours = setTimeout(() => {
    graceEnCours = null;
    if (nbLentes === 0) eteindre();
  }, DUREE_MIN_AFFICHAGE_MS);
  notify();
};

const eteindre = () => {
  if (!lent) return;
  lent = false;
  notify();
};

/**
 * Toutes les requetes ne disent pas quelque chose du debit. Un televersement
 * ou un telechargement dure a proportion du fichier : l'annoncer comme une
 * connexion lente serait un contresens. `ignorerLenteur` est la trappe
 * explicite pour les cas restants, sur le modele du `skipErrorToast` deja en
 * place dans l'intercepteur d'erreurs.
 */
export const doitSuivre = (config = {}) => {
  if (config.ignorerLenteur) return false;
  if (config.responseType === 'blob' || config.responseType === 'arraybuffer') return false;
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) return false;
  return true;
};

export const moniteurReseau = {
  subscribe(cb) {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  },

  estLent() { return lent; },

  debuterRequete(id) {
    if (requetes.has(id)) return;
    const minuteur = setTimeout(() => {
      requetes.set(id, LENTE);
      nbLentes += 1;
      if (!lent) allumer();
    }, SEUIL_LENT_MS);
    requetes.set(id, minuteur);
  },

  terminerRequete(id) {
    if (!requetes.has(id)) return;
    const minuteur = requetes.get(id);
    requetes.delete(id);

    if (minuteur !== LENTE) {
      clearTimeout(minuteur);
      return;
    }

    nbLentes -= 1;
    if (nbLentes === 0 && !graceEnCours) eteindre();
  },
};

let compteur = 0;

const liberer = (config) => {
  if (config && config.__idLenteur) moniteurReseau.terminerRequete(config.__idLenteur);
};

/**
 * Branche le moniteur sur une instance axios. A appeler aussi sur l'instance
 * axios par defaut : `authService`, `renouvellementSession` et `reauthBus`
 * l'utilisent directement pour echapper aux intercepteurs d'`axiosInstance`,
 * or l'ecran de connexion est justement ou une connexion lente se ressent le
 * plus.
 */
export const installerSurAxios = (instance) => {
  instance.interceptors.request.use((config) => {
    if (!doitSuivre(config)) return config;
    compteur += 1;
    config.__idLenteur = `req-${compteur}`;
    moniteurReseau.debuterRequete(config.__idLenteur);
    return config;
  });

  instance.interceptors.response.use(
    (res) => { liberer(res.config); return res; },
    (err) => { liberer(err.config); return Promise.reject(err); },
  );
};

// Helper de test - ne pas utiliser en runtime.
export const __resetMoniteurReseauForTests = () => {
  requetes.forEach((minuteur) => { if (minuteur !== LENTE) clearTimeout(minuteur); });
  clearTimeout(graceEnCours);
  requetes = new Map();
  nbLentes = 0;
  lent = false;
  graceEnCours = null;
  subscribers.clear();
};
